import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, Spinner, Button } from "@vlab-corporation/design-system";
import { AppLayout } from "@/components/AppLayout";
import { MemberTypeBadge } from "@/components/MemberTypeBadge";
import { CreateMemberModal } from "@/components/CreateMemberModal";
import { RegisterTicketModal } from "@/components/RegisterTicketModal";
import { useToast } from "@/components/ToastProvider";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { getMembersList, getReRegistrations } from "@/services/firestore/members";
import { getPinnedMembers, togglePinnedMember } from "@/services/firestore/favorites";
import { isDemoMode } from "@/demo/demoMode";

/** SVG pin icon (filled) */
function PinIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M9.828 1.172a1 1 0 0 1 1.414 0l3.586 3.586a1 1 0 0 1-.707 1.707L12 6.586l-1.293 1.293.707.707a1 1 0 0 1 0 1.414l-2 2a1 1 0 0 1-1.414 0L6 9.999l-3.293 3.293a1 1 0 0 1-1.414-1.414L4.586 8.586 2.586 6.586a1 1 0 0 1 0-1.414l2-2a1 1 0 0 1 1.414 0l.707.707L8 2.586l-.121-.121a1 1 0 0 1 0-1.293z" />
    </svg>
  );
}

/** SVG pin icon (outline) */
function PinOutlineIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M9.828 1.172a1 1 0 0 1 1.414 0l3.586 3.586a1 1 0 0 1-.707 1.707L12 6.586l-1.293 1.293.707.707a1 1 0 0 1 0 1.414l-2 2a1 1 0 0 1-1.414 0L6 9.999l-3.293 3.293a1 1 0 0 1-1.414-1.414L4.586 8.586 2.586 6.586a1 1 0 0 1 0-1.414l2-2a1 1 0 0 1 1.414 0l.707.707L8 2.586l-.121-.121a1 1 0 0 1 0-1.293z" />
    </svg>
  );
}

export function MembersPage() {
  const user = useAuthenticatedUser();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [search, setSearch] = useState("");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isRegisterTicketOpen, setIsRegisterTicketOpen] = useState(false);

  const myUid = user?.uid ?? "";
  const centerOwnerUid = user?.activeMembership.centerOwnerUid ?? "";
  const centerId = user?.activeMembership.centerId ?? 0;
  const centerName = user?.activeMembership.centerName ?? "";
  const instructorId = user?.activeMembership.instructorId ?? "";
  const enabled = !!user;

  const { data: members, isLoading: membersLoading } = useQuery({
    queryKey: ["membersList", centerOwnerUid, centerId],
    queryFn: () => getMembersList(centerOwnerUid, centerId),
    staleTime: 30 * 60 * 1000,
    enabled,
  });

  const { data: reRegistrations } = useQuery({
    queryKey: ["reRegistrations", centerOwnerUid, centerId],
    queryFn: () => getReRegistrations(centerOwnerUid, centerId),
    staleTime: 30 * 60 * 1000,
    enabled,
  });

  const { data: pinnedSet } = useQuery({
    queryKey: ["pinnedMembers", myUid, centerId],
    queryFn: () => getPinnedMembers(myUid, centerId),
    staleTime: 5 * 60 * 1000,
    enabled,
  });

  const pinnedQueryKey = ["pinnedMembers", myUid, centerId];

  const toggleMutation = useMutation({
    mutationFn: (memberId: string) => togglePinnedMember(myUid, centerId, memberId),
    onMutate: async (memberId: string) => {
      // Optimistic update — UI 즉시 반영
      await queryClient.cancelQueries({ queryKey: pinnedQueryKey });
      const previous = queryClient.getQueryData<Set<string>>(pinnedQueryKey);
      const next = new Set(previous ?? []);
      if (next.has(memberId)) {
        next.delete(memberId);
      } else {
        next.add(memberId);
      }
      queryClient.setQueryData(pinnedQueryKey, next);
      return { previous };
    },
    onError: (_err, _memberId, context) => {
      // Rollback on error
      if (context?.previous) {
        queryClient.setQueryData(pinnedQueryKey, context.previous);
      }
    },
    // onSuccess 제거 — optimistic update가 이미 정확한 상태를 유지함.
    // onSuccess에서 서버 응답으로 덮어쓰면 race condition 발생 (read-then-write 패턴)
  });

  const filteredAndSorted = useMemo(() => {
    if (!members) return [];
    const q = search.trim().toLowerCase();
    const filtered = q ? members.filter((m) => m.name.toLowerCase().includes(q)) : members;

    const favSet = pinnedSet ?? new Set<string>();
    return [...filtered].sort((a, b) => {
      const aFav = favSet.has(a.id) ? 0 : 1;
      const bFav = favSet.has(b.id) ? 0 : 1;
      if (aFav !== bFav) return aFav - bFav;
      return a.name.localeCompare(b.name);
    });
  }, [members, search, pinnedSet]);

  if (!user) return null;

  return (
    <AppLayout>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">회원</h2>
        {!isDemoMode() && (
          <div className="flex items-center gap-2">
            <Button variant="primary" size="sm" onClick={() => setIsCreateOpen(true)}>
              회원 등록
            </Button>
            <Button variant="outline" size="sm" onClick={() => setIsRegisterTicketOpen(true)}>
              수강권 등록
            </Button>
          </div>
        )}
      </div>

      {/* 검색 */}
      <div className="mb-4">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="이름 검색..."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-primary-400"
        />
      </div>

      {membersLoading ? (
        <div className="flex justify-center py-8">
          <Spinner size="lg" />
        </div>
      ) : filteredAndSorted.length === 0 ? (
        <Card className="p-6 text-center">
          {search ? (
            <p className="text-sm text-gray-500">"{search}" 검색 결과가 없습니다.</p>
          ) : (
            <div>
              <p className="text-sm text-gray-500 mb-1">등록된 회원이 없습니다.</p>
              <p className="text-xs text-gray-400">관리자에게 Studiomate 동기화를 요청하세요.</p>
            </div>
          )}
        </Card>
      ) : (
        <>
          <p className="text-xs text-gray-400 mb-2">
            {search ? `${filteredAndSorted.length}명 검색됨` : `총 ${filteredAndSorted.length}명`}
          </p>
          <div className="space-y-1.5">
            {filteredAndSorted.map((member) => {
              const isPinned = pinnedSet?.has(member.id) ?? false;
              return (
                <Card
                  key={member.id}
                  className="px-4 py-3 cursor-pointer hover:bg-gray-50 active:bg-gray-100 transition-colors"
                  onClick={() => navigate(`/members/${member.id}`)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm font-medium text-gray-800 truncate">
                        {member.name}
                      </span>
                      <MemberTypeBadge
                        member={member}
                        instructorId={instructorId}
                        reRegistrations={reRegistrations}
                      />
                      {member.contact && (
                        <span className="text-xs text-gray-400">{member.contact}</span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleMutation.mutate(member.id);
                      }}
                      className="flex-shrink-0 p-1 rounded hover:bg-gray-100 transition-colors"
                      aria-label={isPinned ? "상단 고정 해제" : "상단 고정"}
                    >
                      {isPinned ? (
                        <PinIcon className="text-primary-500" />
                      ) : (
                        <PinOutlineIcon className="text-gray-300" />
                      )}
                    </button>
                  </div>
                </Card>
              );
            })}
          </div>
        </>
      )}

      <CreateMemberModal
        isOpen={isCreateOpen}
        centerName={centerName}
        onClose={() => setIsCreateOpen(false)}
        onCreated={() => {
          setIsCreateOpen(false);
          void queryClient.invalidateQueries({
            queryKey: ["membersList", centerOwnerUid, centerId],
          });
          toast.show("회원이 등록되었습니다.", "success");
        }}
      />

      <RegisterTicketModal
        isOpen={isRegisterTicketOpen}
        ownerUid={centerOwnerUid}
        centerId={centerId}
        centerName={centerName}
        onClose={() => setIsRegisterTicketOpen(false)}
        onCreated={() => {
          setIsRegisterTicketOpen(false);
          toast.show("수강권이 등록되었습니다.", "success");
        }}
      />
    </AppLayout>
  );
}
